import ApplicationController from "./application_controller"
import { mapLexiqamusKeyboardEventToOttomanChar } from "./lexiqamus_search/ottoman_keyboard"
import { isOttomanText } from "../lib/ottoman_text_utils"

function isLatinLetterKey(key) {
  return typeof key === "string" && key.length === 1 && /[a-zA-Z]/.test(key)
}

// Presentation modifiers toggled on the chain connector (styled in lexiqamus.scss).
const CHAIN_STATE = {
  hoverable: "word-decoder-chain-connector--hoverable",
  linked:    "word-decoder-chain-connector--linked",
  uncertain: "word-decoder-chain-connector--uncertain"
}

const CHAIN_CONNECTOR_READ_ONLY_CLASS = "word-decoder-chain-connector--read-only"

// Only linked/uncertain are toggled off on state change; hoverable is managed separately.
const CHAIN_MODIFIER_CLASSES = [CHAIN_STATE.linked, CHAIN_STATE.uncertain]

const OTTOMAN_INPUT_CLASS    = "word-decoder-letter-input--ottoman-script"
const CHAR_OVERLAY_CLASS     = "word-decoder-char-overlay"
const MARKER_ANIMATING_CLASS = "word-decoder-uncertainty-marker--animating-out"

// Minimum letter groups on initial load / after URL restore (LexiQamus default: 6).
const INITIAL_LETTER_GROUP_COUNT = 6

// Empty letter group → ZWS in searched_text (LexiQamus wordBuilding: only empty boxes add a gap).
const EMPTY_BOX_PATTERN_CHAR = "\u200B"

// LexiQamus dot-ambiguity / starred-family codes in searched_text (keyboard data-char values).
const WORD_DECODER_GLYPH_PATTERN_CODES = new Set([
  "0", "1", "2", "3", "5", "6", "7", "8", "9", "Ø", "Ù", "Ú", "Û"
])

// Legacy Unicode keyboard tokens → LexiQamus digit codes (backward-compatible URL restore).
const LEGACY_PATTERN_CHAR_MAP = {
  "\uFE91":   "0",
  "\u0626*":  "1",
  "\u0626**": "2",
  "\u062D**": "5",
  "\u062F*":  "7",
  "\u0631*":  "8",
  "\u0633*":  "9",
  "\u0635*":  "Ø",
  "\u0637*":  "Ù",
  "\u0639*":  "6",
  "\u0642*":  "Ú",
  "\u0643*":  "Û"
}

// Multi-char legacy wildcard tokens in searched_text (longest match first).
const PATTERN_WILDCARD_TOKENS = Object.keys(LEGACY_PATTERN_CHAR_MAP)
  .sort((a, b) => b.length - a.length)

// LexiQamus chain auto-link rules (reference: frontend/assets/js/keyboard.js).
const CHAIN_NON_CONNECTOR_CHARS = new Set([
  "ا", "آ", "ء", "أ", "إ", "د", "ذ", "ر", "ز", "ژ", "و", "ؤ", "ە", "ۀ", "ۃ", "ة", "ٱ", "ۏ", "ۊ", "ۋ",
  "7", "8"
])
const CHAIN_CONNECTOR_CHARS = new Set([
  "ب", "پ", "ت", "ث", "ج", "چ", "ح", "خ", "س", "ش", "ص", "ض", "ط", "ظ", "ع", "غ", "ف", "ق", "ك", "ڭ",
  "ی", "گ", "ل", "م", "ن", "ه", "ک", "ي", "ئ",
  "0", "1", "2", "3", "5", "9", "Ø", "Ù", "6", "Ú", "Û"
])

// Returns the chain connector type for a single character. Example: "connector"
function chainConnectorTypeForCharacter(char) {
  if (!char) return "standard"
  if (CHAIN_NON_CONNECTOR_CHARS.has(char)) return "non_connector"
  if (CHAIN_CONNECTOR_CHARS.has(char)) return "connector"
  if (char === "\u221E") return "infinity"
  if (char === "*") return "any_letter"
  return "standard"
}

// Returns the chain connector type for a group of characters. Example: "connector"
function chainConnectorTypeForCharacters(chars) {
  const filled = chars.filter(Boolean)
  if (!filled.length) return "standard"
  if (filled.length === 1) return chainConnectorTypeForCharacter(filled[0])

  let type = "standard"
  for (const char of filled) {
    const next = chainConnectorTypeForCharacter(char)
    if (type === "standard") type = next
    else if (type !== next) return "mixed"
    else type = next
  }
  return type
}

// Returns the chain pair key for a pair of chain connector types.Example: "connector:non_connector"
function chainPairKey(firstType, secondType) {
  return `${firstType}:${secondType}`
}

// LexiQamus rulesWhenUnknown(): when the user resolves an uncertain chain, the
// adjacent */∞ boxes are replaced with chain wildcards in the submitted pattern
// (ĉ connector letter, ȵ non-connector letter, ȹ any letter; premium-only on the backend).
// Keys are chainConnectorType(leftGroup):chainConnectorType(rightGroup).
const CHAIN_FORCED_SUBSTITUTIONS = {
  "connector:infinity": {
    connected:    { second: "\u0239\u221E" }, // ȹ — U+0239 Latin small letter qp with diagonal stroke + ∞
    disconnected: { second: " \u221E" } // ∞ — U+221E Infinity symbol
  },
  "any_letter:connector": {
    connected:    { first: "\u0109" }, // ĉ — U+0109 Latin small letter c with acute
    disconnected: { first: "\u0235" } // ȵ — U+0235 Latin small letter n with hook 
  },
  "any_letter:non_connector": {
    connected:    { first: "\u0109" }, // ĉ — U+0109 Latin small letter c with acute
    disconnected: { first: "\u0235" } // ȵ — U+0235 Latin small letter n with hook 
  },
  "any_letter:any_letter": {
    connected:    { first: "\u0109", second: "\u0239" }, // ĉ — U+0109 Latin small letter c with acute + ȹ — U+0239 Latin small letter qp with diagonal stroke
    disconnected: { first: "\u0235", second: "\u0239" } // ȵ — U+0235 Latin small letter n with hook + ȹ — U+0239 Latin small letter qp with diagonal stroke
  },
  "any_letter:infinity": {
    connected:    { first: "\u0109", second: "\u0239\u221E" }, // ĉ — U+0109 Latin small letter c with acute + ȹ — U+0239 Latin small letter qp with diagonal stroke + ∞
    disconnected: { first: "\u0235", second: "\u0239\u221E" } // ȵ — U+0235 Latin small letter n with hook + ȹ — U+0239 Latin small letter qp with diagonal stroke + ∞
  },
  "infinity:connector": {
    connected:    { first: "\u221E\u0109" }, // ∞ — U+221E Infinity symbol + ĉ — U+0109 Latin small letter c with acute
    disconnected: { first: "\u221E\u0235" } // ∞ — U+221E Infinity symbol + ȵ — U+0235 Latin small letter n with hook
  },
  "infinity:non_connector": {
    connected:    { first: "\u221E\u0109" }, // ∞ — U+221E Infinity symbol + ĉ — U+0109 Latin small letter c with acute
    disconnected: { first: "\u221E\u0235" } // ∞ — U+221E Infinity symbol + ȵ — U+0235 Latin small letter n with hook
  },
  "infinity:any_letter": {
    connected:    { first: "\u221E\u0109", second: "\u0239" }, // ∞ — U+221E Infinity symbol + ĉ — U+0109 Latin small letter c with acute + ȹ — U+0239 Latin small letter qp with diagonal stroke
    disconnected: { first: "\u221E\u0235", second: "\u0239" } // ∞ — U+221E Infinity symbol + ȵ — U+0235 Latin small letter n with hook + ȹ — U+0239 Latin small letter qp with diagonal stroke
  }
}

// Pair key is chainConnectorType(rightGroup):chainConnectorType(leftGroup) — same order as LexiQamus chainStatus().
function chainStatusForPair(pairKey) {
  // Returns the chain status for a pair of chain connector types. Example: "connected"
  if (
    pairKey === "connector:connector" ||
    pairKey === "non_connector:connector" ||
    pairKey === "any_letter:connector"
  ) return "connected"

  if (
    pairKey === "infinity:connector" ||
    pairKey === "connector:any_letter" ||
    pairKey === "non_connector:any_letter" ||
    pairKey === "any_letter:any_letter" ||
    pairKey === "infinity:any_letter" ||
    pairKey === "connector:infinity" ||
    pairKey === "non_connector:infinity" ||
    pairKey === "any_letter:infinity"
  ) return "uncertain"

  if (
    pairKey === "standard:connector" ||
    pairKey === "connector:non_connector" ||
    pairKey === "non_connector:non_connector" ||
    pairKey === "any_letter:non_connector" ||
    pairKey === "infinity:non_connector" ||
    pairKey === "standard:non_connector" ||
    pairKey === "standard:any_letter" ||
    pairKey === "standard:infinity" ||
    pairKey === "connector:standard" ||
    pairKey === "non_connector:standard" ||
    pairKey === "any_letter:standard" ||
    pairKey === "infinity:standard" ||
    pairKey === "standard:standard"
  ) return "disconnected"

  if (pairKey === "mixed:connector") return "connected"
  if (pairKey === "mixed:non_connector") return "disconnected"
  return "mixed"
}

// Word Decoder: tab switching, letter grid, and chain connectors between groups.
// Markup lives in _word_decoder_templates.html.erb; search submits via the BuildLQ results page.
export default class extends ApplicationController {
  static targets = [
    "engineCore",           // Root element of the letter grid
    "tab",                  // Word Decoder / Search mode tab buttons
    "wordDecoderPanel",     // Panel shown when Word Decoder tab is active
    "searchPanel",          // Panel shown when Search tab is active
    "searchResults",        // Ottoman dictionary results (hidden on Word Decoder tab or ENG mode)
    "letterGroupTemplate",  // <template> for a single letter group
    "letterCellTemplate",   // <template> for a single letter cell inside a group
    "separatorTemplate",    // <template> for the separator bar between groups
    "chainConnectorTemplate", // <template> for the chain connector inside a separator
    "chainConnectorStateChangeDeniedMessage" // Shown when chain connector state change is blocked
  ]

  static values = {
    tooltipAddLetter:       String,
    tooltipRemoveLetter:    String,
    tooltipRemoveGroupOne:  String,
    tooltipRemoveGroupAll:  String,
    tooltipInsertGroup:     String,
    tooltipChainSeparate:   String,
    tooltipChainConnected:  String,
    tooltipChainUncertain:  String,
    tooltipChainClickHint:  String,
    allowChainConnectorEditing: Boolean,
    isTurkish: Boolean
  }

  // Builds letter grid, tooltips, and restores tab from URL or HTML active state.
  connect() {
    // Ghost tooltip element is shared across all engine controls (portal pattern).
    this.ghostTooltipEl = document.getElementById("lq-ghost-tooltip")
    this.lastFocusedLetterInput = null
    this.hasLetterGridLayoutChangedFromDefault = false

    if (!this.restoreLetterGridFromUrl()) {
      this.resetLetterGrid()
    }

    this.bindLetterGridEvents()
    this.bindEngineTooltips()

    // If the URL contains ?word_decoder=1, open the Word Decoder tab directly
    // and clean the param from the URL without a page reload.
    if (this.openWordDecoderFromUrl()) {
      this.updateChainConnectorEditability()
      this.hydratePatternGlyphSlots()
      this.syncSearchBarActionButtons()
      return
    }

    // Otherwise restore whichever tab was marked active in the HTML.
    const activeTab = this.hasTabTarget && this.tabTargets.find(tab => tab.classList.contains("lq-mode-tab--active"))
    if (activeTab) this.showPanel(activeTab.dataset.target)
    this.updateChainConnectorEditability()
    this.hydratePatternGlyphSlots()
    this.syncSearchBarActionButtons()
  }

  // Removes engine tooltip listeners and hides the ghost tooltip portal.
  disconnect() {
    this.unbindEngineTooltips()
    this.hideGhostTooltip()
  }

  // Search mode tabs (Word Decoder / Search)
  // TODO: Use a full page reload for all tab switches (not only when a search is active), so URL and UI state stay in sync.
  // Switches Word Decoder / Search tab; reloads page when switching away from an active search.
  switchTab(event) {
    const clickedTab  = event.currentTarget
    const targetPanel = clickedTab.dataset.target

    // Switching to Word Decoder while a search is active requires a full page
    // reload so the URL is cleared and the grid starts fresh.
    if (targetPanel === "word-decoder" && this.hasActiveSearchInUrl()) {
      const url = new URL(window.location.href)
      url.search = ""
      url.searchParams.set("word_decoder", "1")
      window.location.href = url.toString()
      return
    }

    // Switching to Search while a Word Decoder search is active requires the same
    // full page reload so the URL is cleared and the search bar starts fresh.
    if (targetPanel === "search" && this.hasActiveSearchInUrl()) {
      const url = new URL(window.location.href)
      url.search = ""
      window.location.href = url.toString()
      return
    }

    this.activateTab(clickedTab, targetPanel)
  }

  // Updates tab aria state and shows the matching panel.
  activateTab(activeTab, targetPanel) {
    // Sync aria attributes and active class across all tab buttons.
    this.tabTargets.forEach(tab => {
      const isActive = tab === activeTab
      tab.classList.toggle("lq-mode-tab--active", isActive)
      tab.setAttribute("aria-selected", isActive ? "true" : "false")
      tab.setAttribute("tabindex", isActive ? "0" : "-1")
    })

    this.showPanel(targetPanel)
  }

  // True when the page was loaded with a dictionary search in the URL.
  hasActiveSearchInUrl() {
    const searchedText = new URL(window.location.href).searchParams.get("searched_text")
    return searchedText != null && searchedText.trim() !== ""
  }

  /**
   * Opens the Word Decoder tab when the URL indicates a box / Word Decoder search.
   * `word_decoder=1` is a transient flag removed after activation; `search_type=box`
   * is kept so shared links still reopen Word Decoder mode.
   */
  openWordDecoderFromUrl() {
    const url = new URL(window.location.href)
    const isWordDecoderSearch =
      url.searchParams.get("word_decoder") === "1" ||
      url.searchParams.get("search_type") === "box"
    if (!isWordDecoderSearch) return false

    if (url.searchParams.get("word_decoder") === "1") {
      url.searchParams.delete("word_decoder")
      const cleanUrl = url.pathname + (url.search ? url.search : "")
      window.history.replaceState({}, "", cleanUrl)
    }

    const wordDecoderTab = this.tabTargets.find(tab => tab.dataset.target === "word-decoder")
    if (!wordDecoderTab) return false

    this.activateTab(wordDecoderTab, "word-decoder")
    return true
  }

  // True when the results area has either a hits card or the shared no-results card.
  hasSearchResultsContent() {
    if (!this.hasSearchResultsTarget) return false
    return this.searchResultsTarget.querySelector(
      ".main-search-results-card, .js-search-no-results-card"
    ) != null
  }

  // Toggles word-decoder vs search panels and syncs dictionary results visibility.
  showPanel(targetPanel) {
    const isWordDecoder = targetPanel === "word-decoder"
    this.wordDecoderPanelTarget.hidden = !isWordDecoder
    this.searchPanelTarget.hidden      = isWordDecoder
    // ENG definition iframe persists outside the search panel; hide it on Word Decoder switch.
    if (isWordDecoder && typeof searchOutput !== "undefined") searchOutput.hideEngDefinitionIframe()
    // Search results live outside the search panel wrapper, so hide them separately.
    if (this.hasSearchResultsTarget) {
      if (isWordDecoder) {
        // After a Word Decoder search, show results or the same no-results card as simple search.
        this.searchResultsTarget.hidden = !this.hasSearchResultsContent()
      } else {
        // Search tab: let the search bar show, clear, or hide results (e.g. after ENG toggle).
        this.searchBarController()?.syncSearchResultsVisibility()
      }
    }
    // Close floating grid keyboard when leaving word decoder mode.
    if (!isWordDecoder) {
      const kbdCtrl = this.gridKbdController()
      if (kbdCtrl) kbdCtrl.close()
    }
    this.syncSearchBarActionButtons()
    if (typeof searchOutput !== "undefined") {
      if (isWordDecoder) {
        searchOutput.syncSubscriptionRecommendationButtonTheme(false)
      } else {
        searchOutput.syncSubscriptionRecommendationButtonTheme(this.searchBarController()?.sourceType === "eng")
      }
    }
  }

  // Submits the grid pattern through the standard BuildLQ search results page.
  search() {
    this.gridKbdController()?.close()
    this.trimLeadingEmptyLetterGroups()
    this.trimTrailingEmptyLetterGroups()
    this.trimAdjacentEmptyLetterGroups()
    // No force: manually resolved uncertain chains must survive into the pattern.
    this.applyAutoChainConnectors()
    const pattern = this.buildSearchPattern()
    if (!pattern) return

    const url = new URL(window.location.pathname, window.location.origin)
    url.search = ""
    url.searchParams.set("searched_text", pattern)
    url.searchParams.set("search_type", "box")
    url.searchParams.set("word_decoder", "1")

    // LexiQamus myCustomWords: forced chains swap adjacent */∞ boxes for chain
    // wildcards in a second pattern used only for querying (display stays plain).
    if (this.isChainConnectorEditingAllowed()) {
      const chainPattern = this.buildSearchPattern(this.chainForcedOverrides())
      if (chainPattern && chainPattern !== pattern) {
        url.searchParams.set("chain_pattern", chainPattern)
      }

      const chainStates = this.serializeChainStates()
      if (chainStates) url.searchParams.set("chain_states", chainStates)
    }

    // Mirror the search form so dictionary scope matches the Search tab (hidden empty + checked boxes).
    const form = this.element.querySelector("form")
    if (form) {
      new FormData(form).forEach((value, key) => {
        if (key === "dictionary_collection_ids[]") url.searchParams.append(key, value)
      })
    }

    window.location.href = url.toString()
  }

  // ── Chain connector (between letter groups) ────────────────────────────────
  // Click cycle: disconnected → linked → uncertain → disconnected.
  // Hover previews (connect / ?) require --hoverable; removed on click so the
  // resting state does not overlap with hover CSS. Restored on mouseleave.

  handleChainConnectorPointerdown(event) {
    if (this.denyChainConnectorStateChange(event)) return

    event.preventDefault()
    const chainConnector = event.currentTarget
    chainConnector.dataset.chainManual = "true"
    // Remove hoverable before cycling so the hover preview doesn't flash over the new state.
    chainConnector.classList.remove(CHAIN_STATE.hoverable)
    this.cycleChainConnectorState(chainConnector)
    // Update the ghost tooltip immediately to reflect the new state.
    this.showEngineTooltip(chainConnector)
  }

  restoreChainConnectorHoverability(event) {
    if (!this.isChainConnectorEditingAllowed()) return
    // Re-enable hover preview after the pointer leaves the connector.
    event.currentTarget.classList.add(CHAIN_STATE.hoverable)
  }

  isChainConnectorEditingAllowed() {
    return this.allowChainConnectorEditingValue
  }

  denyChainConnectorStateChange(event) {
    if (this.isChainConnectorEditingAllowed()) return false

    if (event) event.preventDefault()
    this.showPremiumSubscriptionRequiredMessageForChainFeature()
    return true
  }

  showPremiumSubscriptionRequiredMessageForChainFeature() {
    if (!this.hasChainConnectorStateChangeDeniedMessageTarget) return

    this.chainConnectorStateChangeDeniedMessageTarget.classList.remove("d-none")
  }

  updateChainConnectorEditability() {
    const locked = !this.isChainConnectorEditingAllowed()
    this.engineCoreTarget.querySelectorAll(".js-word-decoder-chain-connector").forEach(connector => {
      if (locked) {
        connector.classList.add(CHAIN_CONNECTOR_READ_ONLY_CLASS)
        connector.classList.remove(CHAIN_STATE.hoverable)
      } else {
        connector.classList.remove(CHAIN_CONNECTOR_READ_ONLY_CLASS)
      }
    })
  }

  // ── Letter grid structure ────────────────────────────────────────────────
  // Layout: [sep][group][sep][group]…[sep] — chain connectors only on inner separators.

  insertLetterGroup(event) {
    // Insert a new group immediately after the clicked separator.
    const separator = event.currentTarget.closest(".js-word-decoder-separator")
    if (!separator) return

    const newLetterGroup = this.createLetterGroupElement()
    const newSeparator   = this.createSeparatorElement()
    separator.after(newLetterGroup)
    newLetterGroup.after(newSeparator)
    this.syncChainConnectors()
    this.applyAutoChainConnectors()
    this.syncWordDecoderSearchClearButtonVisibility({ structural: true })
    this.focusLetterInput(newLetterGroup.querySelector(".js-word-decoder-letter-input"))
  }

  insertLetterCell(event) {
    event.preventDefault()
    const letterCell = event.currentTarget.closest(".js-word-decoder-letter-cell")
    if (!letterCell) return
    this.insertLetterCellBefore(letterCell)
  }

  removeLetterCell(event) {
    event.preventDefault()
    const letterCell = event.currentTarget.closest(".js-word-decoder-letter-cell")
    if (!letterCell) return

    const frame = letterCell.closest(".js-word-decoder-letter-group-frame")
    letterCell.remove()

    // If the group is now empty, remove the whole group and its trailing separator.
    if (frame && frame.querySelectorAll(".js-word-decoder-letter-cell").length === 0) {
      const letterGroup        = frame.closest(".js-word-decoder-letter-group")
      const trailingSeparator  = letterGroup && letterGroup.nextElementSibling
      if (letterGroup) letterGroup.remove()
      if (trailingSeparator && trailingSeparator.classList.contains("js-word-decoder-separator")) {
        trailingSeparator.remove()
      }
      this.syncChainConnectors()
    }

    this.applyAutoChainConnectors()
    this.syncWordDecoderSearchClearButtonVisibility({ structural: true })
    this.syncSearchBarActionButtons()
  }

  removeLetterGroup(event) {
    event.preventDefault()
    const letterGroup = event.currentTarget.closest(".js-word-decoder-letter-group")
    if (!letterGroup) return

    // Always remove the trailing separator together with the group.
    const trailingSeparator = letterGroup.nextElementSibling
    letterGroup.remove()
    if (trailingSeparator && trailingSeparator.classList.contains("js-word-decoder-separator")) {
      trailingSeparator.remove()
    }
    this.syncChainConnectors()
    this.applyAutoChainConnectors()
    this.syncWordDecoderSearchClearButtonVisibility({ structural: true })
    this.syncSearchBarActionButtons()
  }

  letterGroupHasContent(group) {
    return [...group.querySelectorAll(".js-word-decoder-letter-input")].some(input => this.letterInputChar(input))
  }

  // LexiQamus removeEmptyBox: drop leading empty groups only (not middle empties).
  trimLeadingEmptyLetterGroups() {
    const groups = [...this.engineCoreTarget.querySelectorAll(".js-word-decoder-letter-group")]
    while (groups.length > 1 && !this.letterGroupHasContent(groups[0])) {
      const trailingSep = groups[0].nextElementSibling
      groups[0].remove()
      if (trailingSep?.classList.contains("js-word-decoder-separator")) trailingSep.remove()
      groups.shift()
    }
    this.syncChainConnectors()
    this.applyAutoChainConnectors()
  }

  trimTrailingEmptyLetterGroups({ minGroups = 1 } = {}) {
    const groups = [...this.engineCoreTarget.querySelectorAll(".js-word-decoder-letter-group")]
    for (let i = groups.length - 1; i >= 0; i--) {
      if (this.letterGroupHasContent(groups[i])) break
      if (groups.length <= minGroups) break
      const trailingSep = groups[i].nextElementSibling
      groups[i].remove()
      if (trailingSep?.classList.contains("js-word-decoder-separator")) trailingSep.remove()
      groups.pop()
    }
    this.syncChainConnectors()
    this.applyAutoChainConnectors()
  }

  // LexiQamus main.js search click: drop consecutive empty parent boxes before search.
  trimAdjacentEmptyLetterGroups() {
    let groups = [...this.engineCoreTarget.querySelectorAll(".js-word-decoder-letter-group")]
    let removed = true

    while (removed) {
      removed = false
      for (let i = 0; i < groups.length - 1; i++) {
        if (this.letterGroupHasContent(groups[i]) || this.letterGroupHasContent(groups[i + 1])) continue

        const trailingSep = groups[i].nextElementSibling
        groups[i].remove()
        if (trailingSep?.classList.contains("js-word-decoder-separator")) trailingSep.remove()
        removed = true
        break
      }
      groups = [...this.engineCoreTarget.querySelectorAll(".js-word-decoder-letter-group")]
    }

    this.syncChainConnectors()
    this.applyAutoChainConnectors()
  }

  // After a Word Decoder search reload, refill letter groups (including alternative cells) from searched_text.
  restoreLetterGridFromUrl() {
    const url = new URL(window.location.href)
    if (url.searchParams.get("search_type") !== "box") return false

    const pattern = url.searchParams.get("searched_text")?.trim()
    if (!pattern) return false

    const { slots, uncertainSeparatorIndices } = this.parsePattern(pattern)
    if (!slots.length) return false

    this.resetLetterGrid(slots.length)
    const letterGroups = this.engineCoreTarget.querySelectorAll(".js-word-decoder-letter-group")
    const kbdCells     = this.element.querySelectorAll(".js-lexiqamus-word-decoder-keyboard [data-char]")

    slots.forEach((slot, index) => {
      const letterGroup = letterGroups[index]
      if (!letterGroup || slot === null) return
      this.fillLetterGroup(letterGroup, slot, kbdCells)
    })

    this.trimLeadingEmptyLetterGroups()
    this.trimTrailingEmptyLetterGroups()
    this.applyAutoChainConnectors({ force: true })
    this.applyUncertainChainSeparators(uncertainSeparatorIndices)
    if (this.isChainConnectorEditingAllowed()) {
      this.applyChainStatesFromUrl(url.searchParams.get("chain_states"))
    }
    this.updateChainConnectorEditability()
    return true
  }

  // Persists manually set chain connectors in the URL (c=linked, u=uncertain, d=disconnected).
  // searched_text alone cannot encode connected vs disconnected — only forced uncertain chains
  // affect chain_pattern — so UI state must round-trip separately after search reload.
  serializeChainStates() {
    const separators = [...this.engineCoreTarget.querySelectorAll(".js-word-decoder-separator")]
    const parts      = []

    separators.forEach((separator, index) => {
      if (index === 0 || index === separators.length - 1) return

      const connector = separator.querySelector(".js-word-decoder-chain-connector")
      if (!connector || connector.dataset.chainManual !== "true") return

      let code = "d"
      if (connector.classList.contains(CHAIN_STATE.linked))    code = "c"
      else if (connector.classList.contains(CHAIN_STATE.uncertain)) code = "u"

      parts.push(`${index}:${code}`)
    })

    return parts.length ? parts.join(",") : null
  }

  applyChainStatesFromUrl(encoded) {
    if (!encoded) return

    const separators = [...this.engineCoreTarget.querySelectorAll(".js-word-decoder-separator")]

    encoded.split(",").forEach(part => {
      const [indexStr, code] = part.split(":")
      const index            = Number.parseInt(indexStr, 10)
      if (Number.isNaN(index)) return

      const connector = separators[index]?.querySelector(".js-word-decoder-chain-connector")
      if (!connector) return

      connector.dataset.chainManual = "true"
      if (code === "c")      this.setChainState(connector, CHAIN_STATE.linked)
      else if (code === "u") this.setChainState(connector, CHAIN_STATE.uncertain)
      else                   this.setChainState(connector, CHAIN_STATE.hoverable)
    })
  }

  // Alternative cells in the group frame are LTR DOM; reading order within the box is RTL.
  letterGroupCharactersInReadingOrder(letterGroup) {
    const chars = Array.from(letterGroup.querySelectorAll(".js-word-decoder-letter-input"))
      .map(input => this.letterInputChar(input))
      .filter(Boolean)

    return chars.length > 1 ? [...chars].reverse() : chars
  }

  fillLetterGroup(letterGroup, slot, kbdCells) {
    const chars    = Array.isArray(slot) ? slot : [slot]
    const domOrder = chars.length > 1 ? [...chars].reverse() : chars
    const frame    = letterGroup.querySelector(".js-word-decoder-letter-group-frame")
    if (!frame) return

    frame.replaceChildren()
    domOrder.forEach(char => {
      const cell = this.createLetterCellElement()
      frame.appendChild(cell)
      this.fillLetterInput(cell.querySelector(".js-word-decoder-letter-input"), char, kbdCells)
    })
  }

  normalizePatternChar(char) {
    return LEGACY_PATTERN_CHAR_MAP[char] || char
  }

  fillLetterInput(input, char, kbdCells) {
    if (!input || !char) return
    const patternChar = this.normalizePatternChar(char)
    const kbdCell = [...kbdCells].find(el => el.dataset.char === patternChar)
    const svg     = kbdCell?.querySelector("svg")
    if (svg) this.setLetterInputOverlay(input, svg.outerHTML, patternChar)
    else { input.value = patternChar; this.applyLetterInputScript(input) }
  }

  // Applies LexiQamus chainFunction() rules between adjacent letter groups.
  applyAutoChainConnectors({ force = false } = {}) {
    const groups     = [...this.engineCoreTarget.querySelectorAll(".js-word-decoder-letter-group")]
    const separators = [...this.engineCoreTarget.querySelectorAll(".js-word-decoder-separator")]

    groups.forEach((rightGroup, index) => {
      if (index === 0) return

      const connector = separators[index]?.querySelector(".js-word-decoder-chain-connector")
      if (!connector) return

      const leftGroup = groups[index - 1]
      const pairKey   = chainPairKey(
        this.chainConnectorTypeForGroup(rightGroup),
        this.chainConnectorTypeForGroup(leftGroup)
      )
      const status    = chainStatusForPair(pairKey)

      // LexiQamus dontTouch: manual override persists only while auto status is uncertain.
      if (!force && connector.dataset.chainManual === "true") {
        if (status === "uncertain") return
        delete connector.dataset.chainManual
      }

      this.applyLexiqamusChainStatus(connector, status)
    })
    this.updateChainConnectorEditability()
  }

  chainConnectorTypeForGroup(group) {
    const chars = [...group.querySelectorAll(".js-word-decoder-letter-input")]
      .map(input => this.letterInputChar(input))
    return chainConnectorTypeForCharacters(chars)
  }

  applyLexiqamusChainStatus(connector, status) {
    if (status === "connected")       this.setChainState(connector, CHAIN_STATE.linked)
    else if (status === "uncertain")  this.setChainState(connector, CHAIN_STATE.uncertain)
    else                              this.setChainState(connector, CHAIN_STATE.hoverable)
  }

  readPatternToken(pattern, index) {
    for (const token of PATTERN_WILDCARD_TOKENS) {
      if (pattern.startsWith(token, index)) {
        return { token: this.normalizePatternChar(token), next: index + token.length }
      }
    }
    const char = pattern[index]
    return { token: this.normalizePatternChar(char), next: index + 1 }
  }

  readSlashSeparatedGroup(pattern, start) {
    const chars = []
    let i       = start

    while (i < pattern.length) {
      if (
        pattern[i] === EMPTY_BOX_PATTERN_CHAR ||
        pattern[i] === " " ||
        pattern[i] === "\u0239" ||
        pattern[i] === "("
      ) break

      const { token, next } = this.readPatternToken(pattern, i)
      chars.push(token)
      if (pattern[next] === "/" || pattern[next] === "|") {
        i = next + 1
        continue
      }
      return { chars, next }
    }

    return { chars, next: i }
  }

  // slots: null = empty box; string = single-char group; string[] = alternative group (LexiQamus alternative box).
  parsePattern(pattern) {
    const slots                     = []
    const uncertainSeparatorIndices = []
    let i                           = 0

    while (i < pattern.length) {
      // CPG-358: ZWS before * is a disambiguation marker from buildSearchPattern — skip so * stays its own box on restore.
      if (pattern[i] === EMPTY_BOX_PATTERN_CHAR && pattern[i + 1] === "*") {
        i++
        continue
      }

      if (pattern[i] === EMPTY_BOX_PATTERN_CHAR) {
        slots.push(null)
        i++
        continue
      }

      if (pattern[i] === "\u0239") {
        if (slots.length > 0) uncertainSeparatorIndices.push(slots.length)
        i++
        continue
      }

      if (pattern[i] === " ") {
        i++
        continue
      }

      if (pattern[i] === "(") {
        const closeIndex = pattern.indexOf(")", i + 1)
        if (closeIndex === -1) {
          const { token, next } = this.readPatternToken(pattern, i)
          slots.push(token)
          i = next
          continue
        }

        const alternatives = pattern
          .slice(i + 1, closeIndex)
          .split(/[\/|]/)
          .map(part => part.trim())
          .filter(Boolean)

        if (alternatives.length > 1) slots.push(alternatives)
        else if (alternatives.length === 1) slots.push(alternatives[0])
        i = closeIndex + 1
        continue
      }

      const { token, next } = this.readPatternToken(pattern, i)
      if (pattern[next] === "/" || pattern[next] === "|") {
        const group = this.readSlashSeparatedGroup(pattern, i)
        if (group.chars.length > 1) slots.push(group.chars)
        else if (group.chars.length === 1) slots.push(group.chars[0])
        i = group.next
        continue
      }

      slots.push(token)
      i = next
    }

    return { slots, uncertainSeparatorIndices }
  }

  applyUncertainChainSeparators(separatorIndices) {
    if (!separatorIndices.length) return

    const separators = this.engineCoreTarget.querySelectorAll(".js-word-decoder-separator")
    separatorIndices.forEach(separatorIndex => {
      const connector = separators[separatorIndex]?.querySelector(".js-word-decoder-chain-connector")
      if (!connector) return
      connector.dataset.chainManual = "true"
      this.setChainState(connector, CHAIN_STATE.uncertain)
    })
  }

  // Rebuilds grid; default count matches INITIAL_LETTER_GROUP_COUNT for a fresh grid.
  resetLetterGrid(groupCount = INITIAL_LETTER_GROUP_COUNT) {
    const grid  = this.engineCoreTarget
    const count = Math.max(groupCount, 1)
    grid.replaceChildren()
    // Grid always starts and ends with a separator: [sep][group][sep]…[group][sep]
    grid.appendChild(this.createSeparatorElement())
    for (let i = 0; i < count; i++) {
      grid.appendChild(this.createLetterGroupElement())
      grid.appendChild(this.createSeparatorElement())
    }
    this.syncChainConnectors()
    this.applyAutoChainConnectors()
    this.hasLetterGridLayoutChangedFromDefault = false
    this.syncSearchBarActionButtons()
  }

  // Clones letter-group template with one empty cell inside the frame.
  createLetterGroupElement() {
    const letterGroup = this.cloneTemplate(this.letterGroupTemplateTarget)
    const frame = letterGroup.querySelector(".js-word-decoder-letter-group-frame")
    // Every new group starts with one empty letter cell.
    if (frame) frame.appendChild(this.createLetterCellElement())
    return letterGroup
  }

  // Clones a single letter-cell template.
  createLetterCellElement() {
    return this.cloneTemplate(this.letterCellTemplateTarget)
  }

  // Clones a separator bar (+ insert-group button).
  createSeparatorElement() {
    return this.cloneTemplate(this.separatorTemplateTarget)
  }

  // Clones chain connector in disconnected (hoverable) state.
  createChainConnectorElement() {
    const connector = this.cloneTemplate(this.chainConnectorTemplateTarget)
    this.setChainState(connector, CHAIN_STATE.hoverable)
    return connector
  }

  /**
   * Inserts a new cell to the left of existingCell.
   * The new cell is marked auto-expand so that typing its first character
   * automatically inserts another cell to the left (one-shot expansion).
   */
  insertLetterCellBefore(existingCell) {
    const newCell = this.createLetterCellElement()
    newCell.classList.add("js-word-decoder-auto-expand")
    existingCell.before(newCell)
    this.applyAutoChainConnectors()
    this.syncWordDecoderSearchClearButtonVisibility({ structural: true })
    this.focusLetterInput(newCell.querySelector(".js-word-decoder-letter-input"))
  }

  // Adds/removes chain connectors on inner separators only (not first/last).
  syncChainConnectors() {
    const separators = this.engineCoreTarget.querySelectorAll(".js-word-decoder-separator")
    separators.forEach((separator, index) => {
      const existingConnector = separator.querySelector(".js-word-decoder-chain-connector")
      if (index === 0 || index === separators.length - 1) {
        existingConnector?.remove()
      } else if (!existingConnector) {
        separator.appendChild(this.createChainConnectorElement())
      }
    })
    this.updateChainConnectorEditability()
  }

  // Advances connector: disconnected → linked → uncertain → disconnected.
  cycleChainConnectorState(chainConnector) {
    if (!chainConnector.classList.contains(CHAIN_STATE.linked) &&
        !chainConnector.classList.contains(CHAIN_STATE.uncertain)) {
      // disconnected → linked
      this.setChainState(chainConnector, CHAIN_STATE.linked)
    } else if (chainConnector.classList.contains(CHAIN_STATE.linked)) {
      // linked → uncertain
      this.setChainState(chainConnector, CHAIN_STATE.uncertain)
    } else {
      // uncertain → disconnected (animate the ? marker out before resetting)
      const uncertaintyMarker = chainConnector.querySelector(".js-word-decoder-uncertainty-marker")
      if (uncertaintyMarker) uncertaintyMarker.classList.add(MARKER_ANIMATING_CLASS)
      setTimeout(() => {
        this.setChainState(chainConnector, CHAIN_STATE.hoverable)
        if (uncertaintyMarker) uncertaintyMarker.classList.remove(MARKER_ANIMATING_CLASS)
      }, 260)
    }
  }

  // Applies one chain-connector presentation class, clearing the others.
  setChainState(chainConnector, stateClass) {
    // Always clear linked/uncertain before applying the new state.
    chainConnector.classList.remove(...CHAIN_MODIFIER_CLASSES)
    if (stateClass === CHAIN_STATE.hoverable) {
      chainConnector.classList.add(CHAIN_STATE.hoverable)
      return
    }
    chainConnector.classList.add(stateClass)
  }

  // Ghost tooltips (Word Decoder engine controls)
  // Tooltips are rendered in a single fixed portal element (#lq-ghost-tooltip)
  // rather than per-trigger, to avoid z-index and overflow clipping issues.

  // Attaches mouseenter/leave on the grid for ghost-tooltip portal display.
  bindEngineTooltips() {
    const grid = this.engineCoreTarget
    this.onEngineTooltipEnter = (event) => {
      const trigger = this.findTooltipTrigger(event.target)
      if (!trigger) return
      this.showEngineTooltip(trigger)
    }
    this.onEngineTooltipLeave = (event) => {
      const trigger = this.findTooltipTrigger(event.target)
      if (!trigger) return
      // Don't hide if the pointer moved to a child of the same trigger.
      const related = event.relatedTarget
      if (related && trigger.contains(related)) return
      this.hideGhostTooltip()
    }
    // Use capture phase so events from deeply nested elements bubble up correctly.
    grid.addEventListener("mouseenter", this.onEngineTooltipEnter, true)
    grid.addEventListener("mouseleave", this.onEngineTooltipLeave, true)
  }

  // Removes ghost-tooltip listeners on disconnect.
  unbindEngineTooltips() {
    const grid = this.engineCoreTarget
    if (!grid || !this.onEngineTooltipEnter) return
    grid.removeEventListener("mouseenter", this.onEngineTooltipEnter, true)
    grid.removeEventListener("mouseleave", this.onEngineTooltipLeave, true)
  }

  // Returns the nearest engine control that should show a tooltip.
  findTooltipTrigger(target) {
    if (!target || !target.closest) return null
    // Walk up the DOM to find the nearest engine control that should show a tooltip.
    return target.closest(
      `.js-word-decoder-add-letter-cell, .js-word-decoder-remove-letter-cell, ` +
      `.js-word-decoder-remove-letter-group, .js-word-decoder-insert-group-button, ` +
      `.js-word-decoder-chain-connector`
    )
  }

  // Renders tooltip text into #lq-ghost-tooltip and positions it above the trigger.
  showEngineTooltip(trigger) {
    if (!this.ghostTooltipEl) return
    const text = this.tooltipTextForTrigger(trigger)
    if (!text) return

    // Chain connectors get an extra click-hint line below the main label.
    const hintHtml = trigger.classList.contains("js-word-decoder-chain-connector") &&
      this.isChainConnectorEditingAllowed()
      ? `<span class="lq-ghost-tooltip-hint">${this.tooltipChainClickHintValue}</span>`
      : ""

    const tooltip = this.ghostTooltipEl
    tooltip.classList.remove("lq-ghost-tooltip--active")
    tooltip.innerHTML = `<span class="lq-ghost-tooltip-desc">${text}</span>${hintHtml}`
    tooltip.setAttribute("aria-hidden", "false")
    this.positionGhostTooltip(trigger)
    requestAnimationFrame(() => tooltip.classList.add("lq-ghost-tooltip--active"))
  }

  // Resolves i18n tooltip label for a grid control (+/−/chain/group buttons).
  tooltipTextForTrigger(trigger) {
    if (trigger.classList.contains("js-word-decoder-add-letter-cell")) {
      return this.tooltipAddLetterValue
    }
    if (trigger.classList.contains("js-word-decoder-remove-letter-cell")) {
      return this.tooltipRemoveLetterValue
    }
    if (trigger.classList.contains("js-word-decoder-remove-letter-group")) {
      // Label differs based on whether the group has multiple cells or just one.
      const letterGroup = trigger.closest(".js-word-decoder-letter-group")
      const frame       = letterGroup && letterGroup.querySelector(".js-word-decoder-letter-group-frame")
      const cellCount   = frame ? frame.querySelectorAll(".js-word-decoder-letter-cell").length : 1
      return cellCount > 1 ? this.tooltipRemoveGroupAllValue : this.tooltipRemoveGroupOneValue
    }
    if (trigger.classList.contains("js-word-decoder-insert-group-button")) {
      return this.tooltipInsertGroupValue
    }
    if (trigger.classList.contains("js-word-decoder-chain-connector")) {
      return this.chainConnectorTooltipText(trigger)
    }
    return null
  }

  // Tooltip label for chain connector based on linked/uncertain/disconnected state.
  chainConnectorTooltipText(chainConnector) {
    if (chainConnector.classList.contains(CHAIN_STATE.linked))   return this.tooltipChainConnectedValue
    if (chainConnector.classList.contains(CHAIN_STATE.uncertain)) return this.tooltipChainUncertainValue
    return this.tooltipChainSeparateValue
  }

  // Positions ghost tooltip above trigger, flipping below if clipped at viewport top.
  positionGhostTooltip(trigger) {
    const tooltip     = this.ghostTooltipEl
    const triggerRect = trigger.getBoundingClientRect()
    const tooltipRect = tooltip.getBoundingClientRect()
    const pad         = 10

    // Default: centered above the trigger.
    let top  = triggerRect.top - tooltipRect.height - pad
    let left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2

    // Flip below if there isn't enough space above.
    if (top < pad) top = triggerRect.bottom + pad

    // Clamp horizontally within the viewport.
    if (left < pad) left = pad
    if (left + tooltipRect.width > window.innerWidth - pad) {
      left = window.innerWidth - tooltipRect.width - pad
    }

    tooltip.style.top  = `${top}px`
    tooltip.style.left = `${left}px`
  }

  // Hides the shared ghost tooltip portal (content kept for exit transition).
  hideGhostTooltip() {
    if (!this.ghostTooltipEl) return
    this.ghostTooltipEl.classList.remove("lq-ghost-tooltip--active")
    this.ghostTooltipEl.setAttribute("aria-hidden", "true")
  }

  // ── Letter grid keyboard & input events ──────────────────────────────────

  // Programmatic focus for letter cells (pointerdown, advanceToNextLetterInput, arrow keys, clear).
  // Each typed character moves focus to the next box; the browser scrolls the new input into view
  // by default, which jumps the page on small viewports while the fixed grid keyboard is open.
  // gridKbdController().open() is guarded with isOpen() so open() does not re-scroll per keystroke —
  // preventScroll here is the separate fix for native scroll-on-focus (typing_form#setCursorFocus).
  focusLetterInput(input) {
    if (!input) return
    try {
      input.focus({ preventScroll: true })
    } catch (_e) {
      // FocusOptions not supported; plain focus may scroll on very old browsers.
      input.focus()
    }
  }

  // Wires focus, input, keydown, and grid-keyboard open on letter inputs.
  bindLetterGridEvents() {
    const grid = this.engineCoreTarget

    // Clicking a letter cell focuses its input (opens the floating keyboard via focusin).
    grid.addEventListener("pointerdown", (e) => {
      const cell = e.target.closest(".js-word-decoder-letter-cell")
      if (!cell || e.target.closest("button")) return
      const input = cell.querySelector(".js-word-decoder-letter-input")
      this.focusLetterInput(input)
    })

    // Open floating grid keyboard when a letter input receives focus.
    grid.addEventListener("focusin", (e) => {
      if (!e.target.classList.contains("js-word-decoder-letter-input")) return

      this.lastFocusedLetterInput = e.target
      const kbd = this.gridKbdController()
      // Only first focus opens the panel; later focusin while typing must not re-run open().
      if (!kbd?.isOpen()) kbd?.open(e.target)
    })

    grid.addEventListener("input", (e) => {
      const input = e.target
      if (!input.classList.contains("js-word-decoder-letter-input")) return
      if (this.letterInputHasOverlay(input)) return

      try {
        if (!input.value.trim()) {
          this.applyAutoChainConnectors()
          return
        }

        const typed = input.value.trim()
        const kbdCells = this.element.querySelectorAll(".js-lexiqamus-word-decoder-keyboard [data-char]")
        this.fillLetterInput(input, typed, kbdCells)

        if (!this.letterInputHasOverlay(input)) {
          // Reject pasted or composed Latin letters — only Ottoman script and decoder wildcards are allowed.
          if (!this.isAllowedWordDecoderChar(typed)) {
            input.value = ""
            this.applyLetterInputScript(input)
            this.applyAutoChainConnectors()
            return
          }
        }

        const letterCell = input.closest(".js-word-decoder-letter-cell")
        // Auto-expand: first character typed in an auto-expand cell inserts a new
        // cell to its left and moves focus there for the next letter.
        if (letterCell && letterCell.classList.contains("js-word-decoder-auto-expand")) {
          letterCell.classList.remove("js-word-decoder-auto-expand")
          this.insertLetterCellBefore(letterCell)
          this.applyAutoChainConnectors()
          return
        }
        this.advanceToNextLetterInput(input)
        this.applyAutoChainConnectors()
      } finally {
        this.syncSearchBarActionButtons()
      }
    })

    grid.addEventListener("keydown", (e) => {
      const input = e.target
      if (!input.classList.contains("js-word-decoder-letter-input")) return

      if (e.key === "Enter") {
        e.preventDefault()
        this.search()
        return
      }

      if (e.key === "Backspace" || e.key === "Delete") {
        e.preventDefault()
        this.handleLetterInputBackspaceDelete(input, e.key)
        return
      }

      if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
        e.preventDefault()
        // Screen coordinates: ArrowRight moves visually right (DOM idx-1), ArrowLeft moves visually left (DOM idx+1).
        const domStep = e.key === "ArrowRight" ? -1 : 1
        this.focusLetterInputByDomStep(input, domStep)
        return
      }

      if (e.ctrlKey || e.metaKey || e.isComposing) return

      // Locale-aware QWERTY → Ottoman (shared with search bar). Unmapped Latin keys are blocked.
      if (e.key.length === 1) {
        // Script-producing physical keyboards already supply Ottoman/Arabic in event.key (CPG-179).
        if (isOttomanText(e.key)) return

        const mapped = mapLexiqamusKeyboardEventToOttomanChar({
          key: e.key,
          code: e.code,
          shiftKey: e.shiftKey,
          altKey: e.altKey,
          isTurkishLocale: this.isTurkishValue
        })
        if (mapped) {
          e.preventDefault()
          input.value = mapped
          this.applyLetterInputScript(input)
          input.dispatchEvent(new Event("input", { bubbles: true }))
          return
        }
        if (isLatinLetterKey(e.key)) e.preventDefault()
      }
    }, true)
  }

  // Toggles Ottoman font class when cell contains Arabic-script text.
  applyLetterInputScript(input) {
    // Toggle Ottoman font class based on whether the typed character is Arabic-script.
    const text = input.value.trim()
    input.classList.toggle(OTTOMAN_INPUT_CLASS, text.length > 0 && isOttomanText(text))
  }

  // Clears cell value and any SVG overlay, then fires input event.
  clearLetterInput(input) {
    this.clearLetterInputOverlay(input)
    input.dispatchEvent(new Event("input", { bubbles: true }))
  }

  // True when the cell shows a cloned keyboard SVG instead of plain text.
  letterInputHasOverlay(input) {
    const letterCell = input.closest(".js-word-decoder-letter-cell")
    return !!(letterCell && letterCell.querySelector(`.${CHAR_OVERLAY_CLASS}`))
  }

  // Returns data-char (wildcards/overlays) or the trimmed input value.
  letterInputChar(input) {
    if (input.dataset.char) return this.normalizePatternChar(input.dataset.char)
    const value = input.value.trim()
    return value ? this.normalizePatternChar(value) : value
  }

  // Allows Ottoman script plus decoder wildcards (*, ∞) and ZWS word boundary.
  isAllowedWordDecoderChar(text) {
    if (!text) return false
    if (text === "*" || text === "\u221E" || text === "\u200B") return true
    if (WORD_DECODER_GLYPH_PATTERN_CODES.has(text)) return true
    return isOttomanText(text)
  }

  // Shows keyboard SVG in cell; stores real char in data-char for search pattern.
  setLetterInputOverlay(input, svgHtml, char) {
    const letterCell = input.closest(".js-word-decoder-letter-cell")
    if (!letterCell) return

    this.clearLetterInputOverlay(input)
    input.dataset.char = char
    input.style.color = "transparent"
    input.style.caretColor = "var(--word-decoder-accent)"
    input.value = " "
    input.classList.remove(OTTOMAN_INPUT_CLASS)

    const overlay = document.createElement("div")
    overlay.className = CHAR_OVERLAY_CLASS
    overlay.innerHTML = svgHtml
    letterCell.appendChild(overlay)
    input.dispatchEvent(new Event("input", { bubbles: true }))
    this.applyAutoChainConnectors()
  }

  // Removes SVG overlay and restores normal text input styling.
  clearLetterInputOverlay(input) {
    const letterCell = input.closest(".js-word-decoder-letter-cell")
    if (letterCell) {
      const old = letterCell.querySelector(`.${CHAR_OVERLAY_CLASS}`)
      if (old) old.remove()
    }
    delete input.dataset.char
    input.style.color = ""
    input.style.caretColor = ""
    input.style.opacity = ""
    input.value = ""
    input.classList.remove(OTTOMAN_INPUT_CLASS)
  }

  // Move focus by DOM index step within the ordered letter-input list.
  focusLetterInputByDomStep(currentInput, domStep) {
    const allInputs = Array.from(this.engineCoreTarget.querySelectorAll(".js-word-decoder-letter-input"))
    const target    = allInputs[allInputs.indexOf(currentInput) + domStep]
    this.focusLetterInput(target)
  }

  // After a character is entered, advance to the next box (reference: advanceToNext).
  advanceToNextLetterInput(currentInput) {
    const allInputs    = Array.from(this.engineCoreTarget.querySelectorAll(".js-word-decoder-letter-input"))
    const currentIndex = allInputs.indexOf(currentInput)
    if (currentIndex < allInputs.length - 1) {
      this.focusLetterInput(allInputs[currentIndex + 1])
      return
    }

    // At the last cell: append a new group on the visual-left edge of the RTL grid.
    const separators    = this.engineCoreTarget.querySelectorAll(".js-word-decoder-separator")
    const lastSeparator = separators[separators.length - 1]
    if (!lastSeparator) return

    const newLetterGroup = this.createLetterGroupElement()
    const newSeparator   = this.createSeparatorElement()
    lastSeparator.after(newLetterGroup)
    newLetterGroup.after(newSeparator)
    this.syncChainConnectors()
    this.applyAutoChainConnectors()
    this.syncWordDecoderSearchClearButtonVisibility({ structural: true })
    this.focusLetterInput(newLetterGroup.querySelector(".js-word-decoder-letter-input"))
  }

  // Backspace / Delete: clear current cell, or step to the next filled cell and clear it.
  // Same directions as arrow keys: Backspace → visual right (DOM idx-1), Delete → visual left (DOM idx+1).
  handleLetterInputBackspaceDelete(input, key) {
    const isEmpty = !this.letterInputHasOverlay(input) && input.value.trim() === ""

    if (isEmpty) {
      const allInputs    = Array.from(this.engineCoreTarget.querySelectorAll(".js-word-decoder-letter-input"))
      const currentIndex = allInputs.indexOf(input)
      const step         = key === "Backspace" ? -1 : 1

      for (let i = currentIndex + step; i >= 0 && i < allInputs.length; i += step) {
        if (this.letterInputChar(allInputs[i])) {
          this.clearLetterInput(allInputs[i])
          this.focusLetterInput(allInputs[i])
          this.syncSearchBarActionButtons()
          return
        }
      }
      this.syncSearchBarActionButtons()
      return
    }

    this.clearLetterInput(input)
    this.syncSearchBarActionButtons()
  }

  // Search pattern builder (LexiQamus wordBuilding).
  //   - Empty group → ZWS (only source of word gaps in the pattern)
  //   - Filled groups concatenate regardless of chain icon; unresolved uncertain
  //     connectors contribute nothing (LexiQamus "?" chains)
  //   - Multiple cells in one group → "(a/b)" alternative box (LexiQamus: "(a|b)" in myCustomWords)
  //   - chainOverrides (Map group → string) swaps a group's contribution; used for
  //     the LexiQamus myCustomWords chain pattern (see chainForcedOverrides()).

  buildSearchPattern(chainOverrides = null) {
    const letterGroups = [...this.engineCoreTarget.querySelectorAll(".js-word-decoder-letter-group")]
    let pattern        = ""

    letterGroups.forEach(letterGroup => {
      const characters = this.letterGroupCharactersInReadingOrder(letterGroup)

      if (characters.length === 0) {
        pattern += EMPTY_BOX_PATTERN_CHAR
        return
      }

      if (chainOverrides?.has(letterGroup)) {
        pattern += chainOverrides.get(letterGroup)
        return
      }

      const part = characters.length > 1 ? `(${characters.join("/")})` : characters[0]
      // CPG-358: insert ZWS between starred-family letter and next-box * so URL is "ر\u200B*" not "ر*" (family token).
      if (part === "*" && pattern.length && pattern[pattern.length - 1] !== ")") {
        const lastChar = pattern[pattern.length - 1]
        if (PATTERN_WILDCARD_TOKENS.some(t => t.endsWith("*") && !t.endsWith("**") && t.slice(0, -1) === lastChar)) {
          pattern += EMPTY_BOX_PATTERN_CHAR
        }
      }
      pattern += part
    })

    return pattern
      .replace(/^[ \u200B]+/, "")
      .replace(/[ \u200B]+$/, "")
  }

  // Port of LexiQamus rulesWhenUnknown(): collects per-group chain-wildcard
  // substitutions for connectors the user manually resolved while uncertain.
  // Returns null when no chain was forced (plain pattern is submitted alone).
  chainForcedOverrides() {
    const groups     = [...this.engineCoreTarget.querySelectorAll(".js-word-decoder-letter-group")]
    const separators = [...this.engineCoreTarget.querySelectorAll(".js-word-decoder-separator")]
    const overrides  = new Map()

    groups.forEach((secondGroup, index) => {
      if (index === 0) return

      const connector = separators[index]?.querySelector(".js-word-decoder-chain-connector")
      if (!connector || connector.dataset.chainManual !== "true") return
      if (connector.classList.contains(CHAIN_STATE.uncertain)) return // still unresolved

      const forcedStatus = connector.classList.contains(CHAIN_STATE.linked) ? "connected" : "disconnected"
      const firstGroup   = groups[index - 1]
      const pairKey      = chainPairKey(
        this.chainConnectorTypeForGroup(firstGroup),
        this.chainConnectorTypeForGroup(secondGroup)
      )
      const substitution = CHAIN_FORCED_SUBSTITUTIONS[pairKey]?.[forcedStatus]
      if (!substitution) return

      if (substitution.first)  overrides.set(firstGroup, substitution.first)
      if (substitution.second) overrides.set(secondGroup, substitution.second)
    })

    return overrides.size ? overrides : null
  }

  // Clone keyboard SVG glyphs into SEARCH RESULTS / spelling chips (tooth/star/∞ wildcards).
  hydratePatternGlyphSlots() {
    const root = this.element.closest(".js-lexiqamus-search-results-root")
    if (!root) return

    const keyboard = this.element.querySelector(".js-lexiqamus-word-decoder-keyboard")
    const sources  = keyboard
      ? [...keyboard.querySelectorAll(".js-lq-gk-cell, .js-lq-gk-wildcard-btn")]
      : []

    root.querySelectorAll(".js-word-decoder-pattern-glyph").forEach(slot => {
      const char = this.normalizePatternChar(slot.dataset.char || "")
      if (!char) return

      const source = sources.find(el => el.dataset.char === char)
      let svg      = source?.querySelector("svg")

      if (!svg && char === "\u221E") {
        svg = this.createInfinityPatternGlyphSvg()
      }

      if (!svg) {
        slot.textContent = char
        slot.classList.add("word-decoder-pattern-glyph--text-fallback")
        return
      }

      slot.replaceChildren(svg.cloneNode(true))
    })
  }

  createInfinityPatternGlyphSvg() {
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg")
    svg.setAttribute("width", "44")
    svg.setAttribute("height", "44")
    svg.setAttribute("viewBox", "0 0 44 44")
    svg.setAttribute("aria-hidden", "true")

    const text = document.createElementNS("http://www.w3.org/2000/svg", "text")
    text.setAttribute("x", "22")
    text.setAttribute("y", "28")
    text.setAttribute("text-anchor", "middle")
    text.setAttribute("font-family", "Georgia, serif")
    text.setAttribute("font-size", "22")
    text.setAttribute("font-weight", "400")
    text.setAttribute("fill", "#cc0000")
    text.textContent = "\u221E"
    svg.appendChild(text)
    return svg
  }

  // Grid keyboard integration

  // Receives `lexiqamus-word-decoder-keyboard:insert` from the grid keyboard controller.
  // Inserts the selected character into the most recently focused letter input.
  gridKbdInsert(event) {
    const { char, svgHtml } = event.detail || {}
    if (!char) return
    const input = this.lastFocusedLetterInput
    if (!input || !this.engineCoreTarget.contains(input)) return

    if (svgHtml) {
      this.setLetterInputOverlay(input, svgHtml, char)
      this.advanceToNextLetterInput(input)
      this.syncSearchBarActionButtons()
      return
    }

    if (!this.isAllowedWordDecoderChar(char)) return

    this.clearLetterInputOverlay(input)
    input.value = char
    this.applyLetterInputScript(input)
    // Input handler advances focus to the next box; do not refocus this cell afterward.
    input.dispatchEvent(new Event("input", { bubbles: true }))
  }

  // Receives `lexiqamus-word-decoder-keyboard:backspace` — same as keyboard Backspace key.
  gridKbdBackspace() {
    const input = this.lastFocusedLetterInput
    if (!input || !this.engineCoreTarget.contains(input)) return
    this.handleLetterInputBackspaceDelete(input, "Backspace")
  }

  // Receives `lexiqamus-word-decoder-keyboard:clear-all` — resets while keyboard stays open.
  gridKbdClearAll() {
    this.clearDecoderGrid(true)
  }

  // Outer Clear (left of row): same reset, does not reopen the keyboard.
  outerClearBtnPointerdown() {
    this.clearDecoderGrid(false)
  }

  // Shared reset — back to default empty row; reopenKeyboard keeps panel Clear focused.
  clearDecoderGrid(reopenKeyboard) {
    this.resetLetterGrid()
    if (reopenKeyboard) {
      const firstInput = this.engineCoreTarget.querySelector(".js-word-decoder-letter-input")
      if (firstInput) {
        this.lastFocusedLetterInput = firstInput
        this.focusLetterInput(firstInput)
        const kbd = this.gridKbdController()
        if (!kbd?.isOpen()) kbd?.open(firstInput)
        return
      }
    }
    this.gridKbdController()?.close()
  }

  // Nested search form controller — used when results visibility depends on OTT/ENG mode.
  searchBarController() {
    const el = this.element.querySelector(".js-lexiqamus-search-bar")
    return el ? this.application.getControllerForElementAndIdentifier(el, "lexiqamus-search-bar") : null
  }

  syncSearchBarActionButtons() {
    this.syncWordDecoderSearchClearButtonVisibility()
    this.searchBarController()?.updateActionButtonsVisibility()
  }

  // Default row: 6 groups, 1 cell each, all empty. Outer clear when anything differs.
  syncWordDecoderSearchClearButtonVisibility({ structural = false } = {}) {
    if (structural) this.hasLetterGridLayoutChangedFromDefault = true

    const engine = this.element.querySelector(".word-decoder-engine")
    if (!engine) return

    engine.classList.toggle("word-decoder-engine--clearable", this.wordDecoderSearchClearFromDefaultStateComparison())
  }

  wordDecoderSearchClearFromDefaultStateComparison() {
    const groups = [...this.engineCoreTarget.querySelectorAll(".js-word-decoder-letter-group")]
    return (
      this.hasLetterGridLayoutChangedFromDefault ||
      groups.length !== INITIAL_LETTER_GROUP_COUNT ||
      groups.some(group =>
        group.querySelectorAll(".js-word-decoder-letter-cell").length !== 1 ||
        this.letterGroupHasContent(group)
      )
    )
  }

  // Nested floating grid keyboard controller for Word Decoder letter inputs.
  gridKbdController() {
    const el = this.element.querySelector(".js-lexiqamus-word-decoder-keyboard")
    return el ? this.application.getControllerForElementAndIdentifier(el, "lexiqamus-word-decoder-keyboard") : null
  }

  // Clones first element from a <template> target.
  cloneTemplate(templateTarget) {
    return templateTarget.content.firstElementChild.cloneNode(true)
  }
}
